import { Filter, Plus, Search, UserPlus } from "lucide-react";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WorkerCard } from "@/domains/workers/components/cards";
import { WorkerManagementDialog } from "@/domains/workers/components/dialogs";
import type { WorkerDetailFragment } from "@/gql/graphql";
import { Can } from "@/lib/authorization";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useWorkers } from "../hooks/use-workers";
import { useCreateWorker, useUpdateWorker } from "../hooks/use-workers-mutation";

export const WorkersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [sortBy, setSortBy] = useState<"name" | "hireDate" | "specialties">("name");
  const [showWorkerDialog, setShowWorkerDialog] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<WorkerDetailFragment | null>(null);
  const [togglingWorkers, setTogglingWorkers] = useState<Set<string>>(new Set());
  const [deactivatingWorkers, setDeactivatingWorkers] = useState<Set<string>>(new Set());

  // Get workers data
  const { workers, loading, error, refetch } = useWorkers({
    filters: {
      search: searchTerm || undefined,
      isActive: statusFilter === "all" ? undefined : statusFilter === "active",
      sortBy: sortBy === "name" ? "createdAt" : sortBy === "hireDate" ? "hireDate" : "createdAt",
      sortOrder: "asc",
    },
  });

  // Mutations for managing workers
  const [updateWorker] = useUpdateWorker();
  const [createWorker] = useCreateWorker();

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleAddWorker = () => {
    setSelectedWorker(null);
    setShowWorkerDialog(true);
  };

  const handleEditWorker = (worker: WorkerDetailFragment) => {
    setSelectedWorker(worker);
    setShowWorkerDialog(true);
  };

  const handleToggleOnlineBooking = async (worker: WorkerDetailFragment) => {
    // Prevent multiple clicks while processing
    if (togglingWorkers.has(worker.id)) return;

    try {
      // Add to loading state
      setTogglingWorkers((prev) => new Set(prev).add(worker.id));

      await updateWorker({
        variables: {
          salonId: worker.workerSalons?.[0]?.salonId || "",
          workerId: worker.id,
          input: {
            canAcceptOnlineBookings: !worker.canAcceptOnlineBookings,
          },
        },
        // Let Apollo handle cache updates automatically
        refetchQueries: ["Workers"],
        awaitRefetchQueries: false,
      });
    } catch (error) {
      console.error("Error toggling online booking:", error);
      // TODO: Add proper error handling/toast notification
    } finally {
      // Remove from loading state
      setTogglingWorkers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(worker.id);
        return newSet;
      });
    }
  };

  const handleDeactivateWorker = async (worker: WorkerDetailFragment) => {
    // Prevent multiple clicks while processing
    if (deactivatingWorkers.has(worker.id)) return;

    try {
      // Add to loading state
      setDeactivatingWorkers((prev) => new Set(prev).add(worker.id));

      await updateWorker({
        variables: {
          salonId: worker.workerSalons?.[0]?.salonId || "",
          workerId: worker.id,
          input: {
            isActive: !worker.isActive,
          },
        },
        // Let Apollo handle cache updates automatically
        refetchQueries: ["Workers"],
        awaitRefetchQueries: false,
      });
    } catch (error) {
      console.error("Error updating worker status:", error);
      // TODO: Add proper error handling/toast notification
    } finally {
      // Remove from loading state
      setDeactivatingWorkers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(worker.id);
        return newSet;
      });
    }
  };

  const handleWorkerSaved = async (workerData: Partial<WorkerDetailFragment>) => {
    try {
      const isEditing = !!selectedWorker;

      if (isEditing && selectedWorker) {
        // Update existing worker
        await updateWorker({
          variables: {
            salonId: workerData.workerSalons?.[0]?.salonId || "",
            workerId: selectedWorker.id,
            input: {
              displayName: workerData.displayName,
              bio: workerData.bio,
              color: workerData.color,
              isActive: workerData.isActive,
              canAcceptOnlineBookings: workerData.canAcceptOnlineBookings,
              profileImageUrl: workerData.profileImageUrl,
            },
          },
        });
      } else {
        // Create new worker
        await createWorker({
          variables: {
            salonId: workerData.workerSalons?.[0]?.salonId || "",
            input: {
              displayName: workerData.displayName || "",
              bio: workerData.bio,
              color: workerData.color,
              isActive: workerData.isActive,
              canAcceptOnlineBookings: workerData.canAcceptOnlineBookings,
              profileImageUrl: workerData.profileImageUrl,
            },
          },
        });
      }

      setShowWorkerDialog(false);
      setSelectedWorker(null);
      refetch(); // Refresh the workers list
    } catch (error) {
      console.error("Error saving worker:", error);

      // Extract GraphQL error message for user display
      let errorMessage = "Failed to save worker";
      if (error?.graphQLErrors?.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError?.message) {
        errorMessage = error.networkError.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // TODO: Show error to user via toast notification
      alert(`Error: ${errorMessage}`); // Temporary error display

      throw error; // Re-throw to let the dialog handle the error
    }
  };

  if (loading) {
    return (
      <PageContent>
        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Workers</h1>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-20 bg-gray-200 rounded mb-4" />
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </PageContent>
    );
  }

  if (error) {
    return (
      <PageContent>
        <div className="p-6">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error loading workers: {error.message}</p>
            <Button onClick={refetch}>Try Again</Button>
          </div>
        </div>
      </PageContent>
    );
  }

  // Responsive lead element (search for mobile, empty for desktop)
  const leadElement = (
    <div className="flex items-center gap-2 flex-1 md:hidden">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search workers..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10"
        />
      </div>
      <Button variant="ghost" size="sm" className="h-8 px-2" title="Filters">
        <Filter className="w-4 h-4" />
      </Button>
    </div>
  );

  // Responsive bottom element (filters and search for desktop)
  const bottomElement = (
    <div className="hidden md:flex items-center gap-3 p-4 bg-card rounded-lg shadow-sm border border-border">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search workers..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10 w-80"
        />
      </div>
      <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Workers</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>

      <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Sort by" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="name">Name</SelectItem>
          <SelectItem value="hireDate">Hire Date</SelectItem>
          <SelectItem value="specialties">Specialties</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <>
      <BaseNavigationBar
        title="Workers"
        subtitle="Manage your salon team"
        leadElement={leadElement}
        trailingElements={[
          <Can key="add" I="create" a="Worker">
            <Button size="sm" className="md:whitespace-nowrap" onClick={handleAddWorker}>
              <Plus className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Add</span>
              <span className="hidden md:inline"> Worker</span>
            </Button>
          </Can>,
        ].filter(Boolean)}
        bottomElement={bottomElement}
      />

      <PageContent>
        {/* Workers List */}
        <div className="py-2 space-y-4">
          {workers.map((worker) => (
            <WorkerCard
              key={worker.id}
              worker={worker}
              onEditWorker={handleEditWorker}
              onToggleOnlineBooking={handleToggleOnlineBooking}
              onDeactivateWorker={handleDeactivateWorker}
              isTogglingOnlineBooking={togglingWorkers.has(worker.id)}
              isDeactivating={deactivatingWorkers.has(worker.id)}
            />
          ))}
        </div>

        {/* Empty State */}
        {workers.length === 0 && (
          <div className="text-center py-12">
            <UserPlus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No workers found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search criteria or filters."
                : "Get started by adding your first worker."}
            </p>
            <Can I="create" a="Worker">
              <Button onClick={handleAddWorker}>
                <Plus className="w-4 h-4 mr-2" />
                Add Worker
              </Button>
            </Can>
          </div>
        )}
      </PageContent>

      {/* Worker Management Dialog */}
      <WorkerManagementDialog
        isOpen={showWorkerDialog}
        onClose={() => {
          setShowWorkerDialog(false);
          setSelectedWorker(null);
        }}
        worker={selectedWorker}
        onSave={handleWorkerSaved}
      />
    </>
  );
};
